import { chosen, type Field } from "./forms";

/**
 * ═══ L'AFFICHAGE CONDITIONNEL — un champ qui n'apparaît que si un autre a dit oui ═══
 *
 * Le back office le règle sur chaque placement (`metadata.conditionModelId`, le champ
 * PARENT par l'identifiant de sa question, et `metadata.conditionKeys`, les clés de ses
 * options qui font apparaître celui-ci). La V1 l'applique depuis toujours
 * (`components/FormBuilder.jsx`) ; la V2 CONSERVAIT le réglage à la réécriture du
 * formulaire (voir `formEditor.ts`) mais l'ignorait à l'écran : tout s'affichait.
 * Relevé par William le 2026-09-13.
 *
 * La règle, celle de la V1 : visible si la réponse du parent contient AU MOINS UNE des
 * clés. Une réponse à options est un objet `{ clé: true }` ou une chaîne (voir
 * `chosen`).
 *
 * ⚠️ UN PARENT CACHÉ CACHE SES ENFANTS. « Autre précision » dépend de « Autre », qui
 * dépend de « Venez-vous ? » : décocher la première question doit tout replier, même
 * si la seconde porte encore une vieille réponse. D'où l'itération jusqu'au point
 * fixe, comme le nettoyage de la V1 — et non une seule passe qui dépendrait de l'ordre
 * des champs.
 *
 * ⚠️ UN PARENT INTROUVABLE NE CACHE RIEN. Le back office peut retirer la question
 * parente du formulaire sans nettoyer les conditions qui la visaient : mieux vaut un
 * champ affiché de trop qu'un champ obligatoire que personne ne peut plus atteindre.
 *
 * Pur, donc testé sans écran : l'erreur ne se verrait qu'à l'inscription.
 */

export type Condition = { modelId: number; keys: readonly string[] };

/** Les identifiants des champs à montrer, d'après les réponses du moment. */
export function shownIds(
  fields: ReadonlyArray<Field>,
  valueOf: (field: Field) => unknown,
): Set<string> {
  const byModel = new Map<number, Field>();
  for (const f of fields) if (f.modelId !== null) byModel.set(f.modelId, f);

  let shown = new Set(fields.map((f) => f.id));
  for (;;) {
    const next = new Set<string>();
    for (const f of fields) {
      if (!shown.has(f.id)) continue;
      if (!f.condition) {
        next.add(f.id);
        continue;
      }
      const parent = byModel.get(f.condition.modelId);
      if (!parent || parent.id === f.id) {
        next.add(f.id);
        continue;
      }
      if (!shown.has(parent.id)) continue;
      const answered = chosen(valueOf(parent));
      if (f.condition.keys.some((k) => answered.includes(k))) next.add(f.id);
    }
    if (next.size === shown.size) return next;
    shown = next;
  }
}

/** Les champs à montrer, dans leur ordre. */
export function visibleFields(
  fields: ReadonlyArray<Field>,
  valueOf: (field: Field) => unknown,
): Field[] {
  const shown = shownIds(fields, valueOf);
  return fields.filter((f) => shown.has(f.id));
}
